/**
 * A cell the board can show but not change.
 *
 * Every other cell in this folder is a control: it opens a popover, focuses an
 * input, writes on select. A record field its source refuses to write - who
 * owns a project, how many assets hang off it, when it last moved - has to look
 * like what it is, or the affordance lies and the click that follows does
 * nothing. So the value renders in the same shapes with none of the
 * interaction, rather than an editable cell wearing a `disabled` attribute.
 */
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Badge, badgeVariants } from '@monksflow/monks-ui';
import { CheckIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { workspaceUsersQuery } from '@/entities/project-member';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { ThumbnailImage } from '@/shared/ui/ThumbnailImage';
import { formatRelativeDate } from '@/shared/utils/dateUtils';

import { fileTypeIcon } from '../../lib/file-type-icon';
import { parseLocalDate } from '../../lib/parseLocalDate';
import { toIdList } from '../../lib/people-ids';
import { useRecordUsers } from '../../lib/record-users';
import { statusChipColors } from '../../lib/status-chip-colors';
import type { CustomColumnType, StatusOption } from '../../lib/useTrackersBoard';
import { AvatarStack } from '../AvatarStack';
import { EM_DASH } from './constants';

interface ReadOnlyCellProps {
  type: CustomColumnType | undefined;
  value: unknown;
  /** Only read for a `status` column; empty elsewhere. */
  statusOptions: StatusOption[];
  /**
   * The row's own MIME type, read only by a `files` column: it decides which
   * glyph stands in for a record that has no picture.
   */
  fileType?: unknown;
}

export function ReadOnlyCell({ type, value, statusOptions, fileType }: ReadOnlyCellProps) {
  if (type === 'status') return <ReadOnlyStatus value={value} options={statusOptions} />;
  if (type === 'people') return <ReadOnlyPeople value={value} />;
  if (type === 'tags') return <ReadOnlyTags value={value} />;
  if (type === 'files') return <ReadOnlyPreview value={value} fileType={fileType} />;
  if (type === 'checkbox') return value === true ? <CheckIcon className="size-4" /> : <Empty />;

  const text = type === 'date' ? formatDay(value) : plainText(value);
  if (!text) return <Empty />;
  return (
    <span className="truncate text-sm" title={text}>
      {text}
    </span>
  );
}

function Empty() {
  return <span className="text-muted-foreground">{EM_DASH}</span>;
}

/**
 * A value with no matching option still prints, rather than reading as empty:
 * the source is free to return a status the board's option list has not caught
 * up with, and showing nothing would hide a row's actual state.
 */
function ReadOnlyStatus({ value, options }: { value: unknown; options: StatusOption[] }) {
  const raw = typeof value === 'string' ? value : '';
  if (!raw) return <Empty />;

  const option = options.find((o) => o.id === raw);
  const label = option?.label ?? raw;
  const chipClasses = badgeVariants({
    roundness: 'full',
    className: 'max-w-full overflow-hidden font-normal',
  });

  return (
    <span
      data-slot="status-chip"
      className={option ? chipClasses : `${chipClasses} bg-muted text-muted-foreground`}
      style={option ? statusChipColors(option.color) : undefined}
    >
      {/* Truncates on its own span for the reason given in `StatusCustomCell`. */}
      <span className="truncate" title={label}>
        {label}
      </span>
    </span>
  );
}

/**
 * Two directories, because two kinds of board share this cell. A record board
 * sends its people down with its rows, resolved org-wide, so a project whose
 * owner has left the workspace still names them; a board that holds its own
 * rows assigns from the workspace roster, and that roster is the answer there.
 *
 * The roster is fetched only once there is someone left to name, and every cell
 * on the board shares the one request, which TanStack dedupes by query key.
 */
function ReadOnlyPeople({ value }: { value: unknown }) {
  const workspaceId = useCurrentWorkspaceId();
  const ids = toIdList(value);
  const recordUsers = useRecordUsers();
  const unnamed = ids.filter((id) => !recordUsers.has(id));
  const usersQuery = useQuery({
    ...workspaceUsersQuery(workspaceId),
    enabled: Boolean(workspaceId) && unnamed.length > 0,
  });

  if (ids.length === 0) return <Empty />;
  const users = [
    ...ids.flatMap((id) => recordUsers.get(id) ?? []),
    ...(usersQuery.data?.data ?? []),
  ];
  return <AvatarStack ids={ids} users={users} />;
}

/**
 * A record's picture, from the URL its source signed for this page load.
 *
 * Two ways there is no picture, and both end at the same glyph rather than at
 * an em dash: the record never had one, or the signature it was fetched under
 * has expired, which a board left open all day should expect. The glyph is
 * chosen from the row's file type, so the slot still says what kind of thing
 * the row is instead of only that it has no thumbnail.
 */
function ReadOnlyPreview({ value, fileType }: { value: unknown; fileType: unknown }) {
  const { t } = useTranslation('trackers');
  const src = typeof value === 'string' ? value : '';

  const Icon = fileTypeIcon(typeof fileType === 'string' ? fileType : '');
  const glyph = (
    <span className="flex size-8 items-center justify-center rounded bg-muted">
      <Icon className="size-4 text-muted-foreground" aria-hidden />
    </span>
  );

  if (!src) return glyph;
  return (
    <ThumbnailImage
      src={src}
      alt={t('trackersBoard.readOnlyCell.preview')}
      className="size-8 rounded"
      fallback={glyph}
    />
  );
}

function ReadOnlyTags({ value }: { value: unknown }) {
  const tags = Array.isArray(value)
    ? value.filter((v): v is string => typeof v === 'string' && !!v)
    : [];
  if (tags.length === 0) return <Empty />;

  return (
    <span className="flex items-center gap-1 overflow-hidden">
      {tags.map((tag) => (
        <Badge
          key={tag}
          variant="outline"
          roundness="full"
          className="max-w-full shrink-0 truncate font-normal"
        >
          {tag}
        </Badge>
      ))}
    </span>
  );
}

/**
 * A bare `YYYY-MM-DD` is a day someone chose and prints as one. A full
 * timestamp is something that happened, and "3 days ago" answers the question
 * it is on the board to answer - how stale is this row - in a way a date the
 * reader has to subtract from today does not.
 */
function formatDay(value: unknown): string {
  if (typeof value !== 'string' || !value) return '';
  if (value.length > 10) return formatRelativeDate(value);
  const parsed = parseLocalDate(value);
  // eslint-disable-next-line i18next/no-literal-string -- date-fns format pattern, machine input to format() rather than translator-facing copy
  return parsed ? format(parsed, 'MMM d, yyyy') : '';
}

/**
 * Multi-valued fields (a project's tags) arrive as an array and read as a list.
 */
function plainText(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  if (Array.isArray(value)) {
    return value
      .filter((v): v is string | number => typeof v === 'string' || typeof v === 'number')

      .join(', ');
  }
  return '';
}
